import type { Nullable } from "../types";
import type { AbsenceListItem } from "../types/absences";

/**
 * Whether an absence may still be edited or withdrawn, and by whom.
 *
 * An employee changes their own vacation request only while it has not started: once the first
 * day is here the request has become the record of what happened, and other categories — sick
 * leave and the like — are records from the moment they are filed. An administrator changes any
 * absence, past or present, whatever its category; correcting the record is what administration
 * is for.
 *
 * Which of the two a caller is, is decided from their rules — an unrestricted `update Absence`,
 * which is `manage all` — and not from this function, so the same answer holds on the server,
 * where it is enforced, and in the client, where it hides the controls.
 *
 * @param absence - The absence to evaluate.
 * @param role - `"admin"` for a caller whose `update Absence` carries no condition; `"user"` for
 *   everybody else, whose right is bound to their own rows.
 * @returns `true` when the caller may edit or withdraw the absence.
 */
export function getIsAbsenceEditable(
	absence: Nullable<
		Pick<AbsenceListItem, "startDate"> & { category: AbsenceListItem["category"] | null }
	>,
	role: "user" | "admin",
): boolean {
	if (!absence) return false;
	if (role === "admin") return true;
	const today = new Date().toISOString().slice(0, 10);
	if (absence.category !== "vacation") return false;
	return absence.startDate > today;
}
